import { IMAGE_PATHS } from 'constants/imagePaths';
import { useFormik } from 'formik';
import useTranslate from 'hooks/useTranslate';
import { ReactNode, useMemo, useRef } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import ReactSelect from 'react-select';
import { useGetCommonDataQuery } from 'services';
import { checkObjHasValues, LANGUAGE_PROFICIENCY } from 'utils';
import * as Yup from 'yup';
import ActionMenu from '../ActionMenu';
import AddMoreButton from '../AddMoreButton';
import useResumeStore from 'store/resume';

interface LanguageInterface {
	name: string;
	proficiency: string;
}

const Language = ({
	data,
	slug,
	handleRemoveClick,
	onHandleChange,
	handleRemoveValues,
	handleModalVisibility,
	handleTitleChange,
	checkLimitExceed,
}: any) => {
	const { translate } = useTranslate();
	const proficiencyRef = useRef<any>(null);

	const { data: commonData } = useGetCommonDataQuery<any>();
	const { languages } = commonData ?? {};

	const languagesArr = useMemo(() => {
		const arr = [{ id: '', name: translate('PlaceHolder.language') }];
		if (languages?.length) {
			arr.push(...languages);
		}
		return arr;
	}, [languages]);

	const storedLanguagesData = useResumeStore((state) => state.resumeData?.language ?? []);
	const fieldVisibility = useResumeStore(
		(state) => state?.resumeData?.manageTemplateFields?.[slug] || false,
	);

	const validationSchema = Yup.object().shape({
		name: Yup.string().required(
			translate('Validation.required', { field: translate('PlaceHolder.language') }),
		),
		proficiency: Yup.string().required(
			translate('Validation.required', { field: translate('PlaceHolder.proficiency') }),
		),
	});

	const formik = useFormik({
		initialValues: {
			name: '',
			proficiency: '',
		},
		validationSchema: validationSchema,
		onSubmit: (values: any) => {
			onHandleChange(slug, values?.name, values?.proficiency);
			formik.resetForm();
		},
	});

	const renderError = <T extends keyof LanguageInterface>(field: T) =>
		formik.touched[field] && formik.errors[field] ? (
			<span className="text-danger f-error">{formik.errors[field] as ReactNode}</span>
		) : null;

	const checkLimits = useMemo(() => {
		return checkLimitExceed(slug);
	}, [data?.length, fieldVisibility]);

	return (
		<div className="resume-info">
			<Form onSubmit={formik.handleSubmit} autoComplete="off">
				<div className="build-resume-edit-header">
					<ActionMenu
						data={data}
						handleRemoveClick={handleRemoveClick}
						slug="langTitle"
						contentSlug={slug}
						handleModalVisibility={handleModalVisibility}
						formikObj={formik}
						handleTitleChange={handleTitleChange}
					/>
					<p className="mb-0">{translate('Resume.languageSubtitle')}</p>
				</div>
				{fieldVisibility && (
					<div className="build-resume-edit-fields">
						<Row>
							{checkLimits && (
								<>
									<Col xl="6">
										<div className="text-start">
											<Form.Group className="mb-4 position-relative">
												<Form.Label className="lh-1">
													{translate('PlaceHolder.language')}
												</Form.Label>
												<ReactSelect
													className="basic-single"
													classNamePrefix="select"
													isSearchable
													name="name"
													options={languagesArr}
													getOptionValue={(option: any) => option.id}
													getOptionLabel={(option: any) => option.name}
													onChange={(option: any) => formik.setFieldValue('name', option?.id)}
													onBlur={formik.handleBlur}
													isOptionDisabled={(option: any) =>
														storedLanguagesData.some((item: any) => item?.id === option?.id)
													}
													autoFocus={false}
													placeholder={translate('PlaceHolder.selectLanguage')}
													value={languagesArr?.find((item: any) => item.id === formik.values.name)}
												/>
												{renderError('name')}
											</Form.Group>
										</div>
									</Col>
									<Col xl="6">
										<div className="text-start">
											<Form.Group className="mb-4 position-relative">
												<Form.Label className="lh-1">
													{translate('PlaceHolder.proficiency')}
												</Form.Label>
												<ReactSelect
													isSearchable
													className="basic-single"
													classNamePrefix="select"
													name="proficiency"
													options={LANGUAGE_PROFICIENCY(translate)}
													ref={proficiencyRef}
													onChange={(option: any) =>
														formik.setFieldValue('proficiency', option?.value)
													}
													onBlur={formik.handleBlur}
													value={LANGUAGE_PROFICIENCY(translate).find(
														(item: any) => item.value === formik.values.proficiency,
													)}
													placeholder={translate('PlaceHolder.selectProficiency')}
													data-language={formik.values.name}
												/>
												{renderError('proficiency')}
											</Form.Group>
										</div>
									</Col>
									<div>
										<AddMoreButton title={translate('Resume.addLanguage')} />
									</div>
								</>
							)}
							<Col>
								<div className="selected-items d-flex flex-wrap gap-3 mt-4">
									{data?.length > 0 &&
										data?.map((field: any, index: number) => {
											if (checkObjHasValues(field)) {
												const proficiencyLabel = LANGUAGE_PROFICIENCY(translate)?.find(
													(item: any) => item.value === field?.proficiency,
												)?.label;
												return (
													<div
														key={`${slug}-${index}`}
														className="selected-items-box d-flex align-items-center"
													>
														{field?.name} ({proficiencyLabel}){' '}
														<Button
															variant="link"
															className="p-0 delete-info text-decoration-none btn-link border-0 rounded-circle"
															onClick={() => handleRemoveValues(slug, field?.id)}
														>
															<i className="ri-close-line"></i>
															{/* <img src={IMAGE_PATHS?.cross} alt="edit" className="ms-2" /> */}
														</Button>
													</div>
												);
											}
										})}
								</div>
							</Col>
						</Row>
					</div>
				)}
			</Form>
		</div>
	);
};

export default Language;
